import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { XCloseIcon } from '@rsos/assets/icons';
import { CALLS } from '../../../utils/search/constants';
import {
  Container,
  Form,
  InputContainer,
  IconWrapper,
  SearchInput,
  ClearSearch,
} from './UniversalSearch.styles';

function shouldNotRerender(prevProps, nextProps) {
  // prevent search field flickers when switching searchTypes from dropdown
  const prevSearchTerm = prevProps.searchTerm;
  const nextSearchTerm = nextProps.searchTerm;
  if (
    (prevProps.filteredCallResults.length ===
      nextProps.filteredCallResults.length &&
      prevProps.gisSearchResults.length === nextProps.gisSearchResults.length &&
      prevProps.isClearSearchVisible === nextProps.isClearSearchVisible &&
      prevProps.mouseCursor === nextProps.mouseCursor &&
      prevProps.hasError === nextProps.hasError &&
      prevSearchTerm &&
      nextSearchTerm &&
      prevSearchTerm === nextSearchTerm) ||
    (!prevSearchTerm && !nextSearchTerm) ||
    (nextProps.searchType && !nextSearchTerm)
  )
    return true;
  return false;
}

const UniversalSearch = memo(
  ({
    clearOutError,
    handleSearchResult,
    handleSearchOutboundSMS,
    hasError,
    mouseCursor,
    searchBarRef,
    searchTerm,
    searchType,
    setIsClickedFromFilter,
    setMouseCursor,
    setSearchTerm,
    handleSelectedAddress,
    resultContainerRef,
    gisSearchResults = [],
    filteredCallResults = [],
    filteredOutboundSMSResults = [],
    filteredAxonResults = [],
    onSearch,
    onReset,
    handleAxonSearchResult,
  }) => {
    /**
     * Handle user typing in the universal search box
     */
    const handleOnChange = e => {
      const { value } = e.target;
      clearOutError();
      // TODO_FALCON Debounce

      if (e.target.value === '') {
        onReset();
      } else {
        setSearchTerm(value);
      }
    };
    const handleOnSubmit = e => {
      setIsClickedFromFilter(false);
      if (e) {
        e.preventDefault();
      }
      // Report the search term and search type.
      clearOutError();
      onSearch(searchTerm, searchType);
    };

    const results = [
      ...filteredAxonResults,
      ...filteredCallResults,
      ...filteredOutboundSMSResults,
      ...gisSearchResults,
    ];
    const handleKeyDown = e => {
      const KEY_ARROWUP = 38;
      const KEY_ARROWDOWN = 40;
      const KEY_BACKSPACE = 8;
      const KEY_ENTER = 13;
      const TOTAL_LENGTH = results.length;
      if (e.keyCode === KEY_BACKSPACE) {
        clearOutError();
      }

      const parentRect = resultContainerRef.current?.getBoundingClientRect();
      if (e.keyCode === KEY_ARROWDOWN && mouseCursor < TOTAL_LENGTH - 1) {
        setMouseCursor(mouseCursor + 1);

        if (resultContainerRef.current) {
          const child =
            resultContainerRef?.current?.childNodes?.[mouseCursor + 1];
          const childRect = child?.getBoundingClientRect();
          if (childRect?.top - childRect?.height > parentRect?.height) {
            child.scrollIntoView({ block: 'end' });
          }
        }
      }
      if (e.keyCode === KEY_ARROWUP && mouseCursor > 0) {
        setMouseCursor(mouseCursor - 1);

        if (resultContainerRef.current) {
          const child =
            resultContainerRef?.current?.childNodes[mouseCursor - 1];
          const childRect = child?.getBoundingClientRect();
          if (childRect?.top < parentRect?.top) {
            child.scrollIntoView({ block: 'start' });
          }
        }
      }
      if (e.keyCode === KEY_ENTER && TOTAL_LENGTH > 0 && searchTerm.length) {
        const hoveredItem = results.find(
          (item, index) => mouseCursor === index,
        );
        if (hoveredItem?.data?.source === 'axon') {
          const coordinates =
            hoveredItem?.data?.shape?.features?.[0]?.geometry?.coordinates;
          handleAxonSearchResult({
            coordinates,
            axon: hoveredItem?.data,
          });
        } else if (hoveredItem?.data?._datasetName === CALLS) {
          setIsClickedFromFilter(true);
          const hoveredCallerID =
            hoveredItem.data.core?.locations[0]?.caller_id ||
            hoveredItem.data.core?.secondaryLocations[0]?.caller_id;
          handleSearchResult(hoveredCallerID);
        } else if (hoveredItem?.data?.callerID) {
          setIsClickedFromFilter(true);
          const hoveredCallerID = hoveredItem.data.callerID;
          handleSearchOutboundSMS(hoveredCallerID);
        } else if (hoveredItem?.address) {
          handleSelectedAddress(hoveredItem);
        }
      } else if (e.keyCode === KEY_ENTER && TOTAL_LENGTH === 0) {
        handleOnSubmit(e);
      }
    };

    return (
      <Container>
        <InputContainer>
          <Form
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <SearchInput
              ref={searchBarRef}
              placeholder="Search"
              onChange={handleOnChange}
              onKeyDown={handleKeyDown}
              value={searchTerm}
              hasError={hasError}
              data-name="universal-search"
            />
            <IconWrapper>
              {searchTerm && (
                <ClearSearch onClick={onReset}>
                  <XCloseIcon />
                </ClearSearch>
              )}
            </IconWrapper>
          </Form>
        </InputContainer>
      </Container>
    );
  },
  shouldNotRerender,
);

UniversalSearch.propTypes = {
  clearOutError: PropTypes.func,
  onReset: PropTypes.func,
  hasError: PropTypes.bool,
  isClearSearchVisible: PropTypes.bool,
  isDropdownClicked: PropTypes.bool,
  mouseCursor: PropTypes.number,
  searchBarRef: PropTypes.object,
  searchLocation: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  filteredCallsResult: PropTypes.array,
  filteredAxonResults: PropTypes.array,
  searchType: PropTypes.string,
  setIsClickedFromFilter: PropTypes.func,
  setMouseCursor: PropTypes.func,
  setSearchTerm: PropTypes.func,
  setSearchType: PropTypes.func,
};

export default UniversalSearch;
